import React, { useEffect, useState } from 'react'
import { useComparatore, useTrans, useTealium, useIsMobile } from '@next-nx/hooks'
import { getTimestamp } from '@next-nx/utils'
import { Bin, Shuffle } from '@next-nx/shared-ui/icons'
import { Button, Modal, WContainer } from '@next-nx/shared-ui/atoms'
import styles from './CompareBar.module.sass'

interface Props {
  type?: 'plp' | 'pdp'
  collapsed: boolean
  setCollapsed: (v: boolean) => void
  url?: string
  className?: string
}

const CompareBar = (props: Props) => {
  const {
    type = 'plp',
    collapsed = true,
    setCollapsed = () => undefined,
    url = '/comparatore',
    className = '',
  } = props

  const t = useTrans()
  const isMobile = useIsMobile()

  const {
    maxCompareItems,
    compareItems: items,
    limitReached,
    setLimitReached,
    setCompareItems: setItems,
    firstTime,
    addedItem,
    setAddedItem,
  } = useComparatore()
  const { linkDataLayer } = useTealium()

  const [openHowToModal, setOpenHowToModal] = useState(false)

  const handleTracking = (product: string = '', action_type?: string, interazione?: string) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}

    switch (interazione) {
      case 'utilizzo':
        dlObj = {
          virtual: 'action',
          action_name: 'comparatore',
          action_tech: 'click',
          action_type,
          action_timestamp,
          action_effect: 'utilizzo_comparatore',
          cta: 'sticky_layer',
          product: `${items?.length}_${items
            ?.map((x) => x.nome)
            .toString()
            .replace(/,/g, '|')}`,
        }
        break
      default:
        dlObj = {
          virtual: 'action',
          action_name: 'comparatore',
          action_tech: 'click',
          action_type: 'rimuovi',
          action_timestamp,
          action_effect: 'rimozione_veicolo_comparatore',
          cta: 'rimuovi_sticky_layer',
          product: product?.replace(/ /g, '_').replace(/\./g, '').toLowerCase(),
        }
        break
    }
    linkDataLayer(dlObj)
  }

  useEffect(() => {
    firstTime && type !== 'pdp' && setOpenHowToModal(true)
  }, [firstTime])

  const BarItem = ({ item, i }) => {
    return (
      <div
        className={`${styles.selection} ${
          items.length >= i + 1 ? styles['selection--active'] : ''
        }`}
      >
        <span className={styles.selectionIndex}>{i + 1}</span>
        {items.length >= i + 1 ? (
          <span className={`${styles.selectionValue}`}>{item?.nome}</span>
        ) : (
          <button
            role="button"
            className={`${styles.selectionValue} ${styles['selectionValue--button']}`}
            onClick={() => setOpenHowToModal(true)}
          >
            {t('Nessuna selezione')}
          </button>
        )}
        {items.length >= i + 1 && (
          <button
            role="button"
            className={styles.selectionDelete}
            onClick={() => {
              setItems(items.filter((item, k) => k !== i))
              handleTracking(item?.nome)
            }}
          >
            <Bin />
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      {(type !== 'pdp' || maxCompareItems !== 2) && (
        <Modal
          open={openHowToModal}
          setOpen={setOpenHowToModal}
          closeButton
          title={t('Utilizza il nostro comparatore per confrontare i veicoli!')}
          description={t(
            'Sei indeciso tra diversi veicoli? Con il nostro comparatore puoi confrontare le diverse caratteristiche.'
          )}
          buttons={<Button label={t('Ok, capito')} onClick={() => setOpenHowToModal(false)} />}
          className={`${styles.modalRoot}`}
          bodyClassName={styles.modalBody}
        >
          <div>
            <div className={`${styles.selection}`}>
              <span className={`${styles.selectionIndex} ${styles['selectionIndex--modal']}`}>
                1
              </span>
              <div>
                <span className={`${styles.descriptionIndex}`}>
                  {t('Aggiungi i veicoli al comparatore tramite l’icona')}
                </span>
                <Button icon={<Shuffle />} disabled iconPos="right" className={styles.compareCta} />
              </div>
            </div>
            <div className={`${styles.selection}`}>
              <span className={`${styles.selectionIndex} ${styles['selectionIndex--modal']}`}>
                2
              </span>
              <span className={`${styles.descriptionIndex}`}>
                {t('I veicoli aggiunti nel comparatore saranno visibili nella barra blu in basso')}
              </span>
            </div>
            <div className={`${styles.selection}`}>
              <span className={`${styles.selectionIndex} ${styles['selectionIndex--modal']}`}>
                3
              </span>
              <span className={`${styles.descriptionIndex}`}>
                {t('Rimuovi e aggiungi veicoli fino ad un massimo di {0}', [
                  maxCompareItems.toString(),
                ])}
              </span>
            </div>
            <div className={`${styles.selection}`}>
              <span className={`${styles.selectionIndex} ${styles['selectionIndex--modal']}`}>
                4
              </span>
              <span className={`${styles.descriptionIndex}`}>
                {t(
                  'Infine, clicca su “Confronta” per mettere a confronto le caratteristiche dei veicoli selezionati'
                )}
              </span>
            </div>
          </div>
        </Modal>
      )}
      <Modal
        open={limitReached}
        setOpen={setLimitReached}
        closeButton
        title={t('Puoi confrontare fino ad un massimo di {0} veicoli', [
          maxCompareItems.toString(),
        ])}
        description={
          type === 'pdp'
            ? t(
                'Hai già {0} veicoli aggiunti al comparatore. Per gestire i tuoi veicoli ed eliminarne uno clicca su “Vai al comparatore”.',
                [maxCompareItems.toString()]
              )
            : t(
                'Hai già {0} veicoli aggiunti al comparatore, rimuovine uno per aggiungere questo veicolo al confronto.',
                [maxCompareItems.toString()]
              )
        }
        buttons={
          type === 'pdp' ? (
            <>
              <Button label={t('Chiudi')} variant="ghost" onClick={() => setLimitReached(false)} />
              <Button label={t('Vai al comparatore')} href={url} />
            </>
          ) : (
            <Button label={t('Ok, capito')} onClick={() => setLimitReached(false)} />
          )
        }
        className={`${styles.modalRoot}`}
        bodyClassName={styles.modalBody}
      ></Modal>
      {type === 'pdp' && isMobile && (
        <Modal
          open={addedItem}
          setOpen={setAddedItem}
          closeButton
          title={t('Hai aggiunto un veicolo alla comparazione', [maxCompareItems.toString()])}
          description={t(
            'Il tuo veicolo è stato aggiunto al comparatore. Per scroprine le specifiche clicca su “Vai al comparatore”.'
          )}
          buttons={
            <>
              <Button label={t('Chiudi')} variant="ghost" onClick={() => setAddedItem(false)} />
              <Button
                label={t('Vai al comparatore')}
                href={url}
                onClick={() => setAddedItem(false)}
              />
            </>
          }
          className={`${styles.modalRoot}`}
          bodyClassName={styles.modalBody}
        ></Modal>
      )}
      {(type !== 'pdp' || maxCompareItems !== 2) && (
        <>
          <button
            role="button"
            onClick={() => setCollapsed(false)}
            className={`${styles.trigger} ${type === 'pdp' ? styles['trigger--pdp'] : ''} ${
              collapsed ? styles['trigger--visible'] : ''
            }`}
          >
            <span className={styles.triggerIcon}>
              <Shuffle />
            </span>
            {items.length > 0 && <span className={styles.triggerBadge}>{items.length}</span>}
          </button>
          <div
            className={`${styles.root} ${type === 'pdp' ? styles['root--pdp'] : ''} ${
              !collapsed ? styles['root--visible'] : ''
            } ${className}`}
          >
            <WContainer variant="lg">
              <div className={styles.layout}>
                <div className={styles.actions}>
                  <button
                    role="button"
                    className={styles.closeButton}
                    onClick={() => {
                      setCollapsed(true)
                      handleTracking('', 'nascondi_tutto', 'utilizzo')
                    }}
                  >
                    <span className={styles.closeButtonLabelDesktop}>{t('Nascondi tutto')}</span>
                    <span className={styles.closeButtonLabelMobile}>
                      {t('Nascondi comparatore')}
                    </span>
                  </button>
                  <Button
                    label={t('Confronta veicoli')}
                    labelMobile={t('Confronta')}
                    href={url}
                    className={styles.cta}
                    onClick={() => handleTracking('', 'confronta_veicoli', 'utilizzo')}
                  />
                </div>
                <div className={styles.title}>
                  <span className={styles.titleIcon}>
                    <Shuffle />
                  </span>
                  <span className={styles.titleLabel}>
                    {t('Confronta fino a {0} veicoli:', [maxCompareItems.toString()])}
                  </span>
                </div>
                <div className={styles.selections}>
                  {[...Array(maxCompareItems)].map((x, i) => (
                    <BarItem key={i} item={items?.[i]} i={i} />
                  ))}
                </div>
              </div>
            </WContainer>
          </div>
        </>
      )}
    </>
  )
}

export default CompareBar
